import type { components } from '@fluencelabs/vodopad-admin-api'
import { useQueryClient } from '@tanstack/react-query'
import { App, Form, Input, InputNumber, Modal, Select } from 'antd'
import { useState } from 'react'

import client from '@/api/client'
import { mutateNoBody } from '@/api/mutateNoBody'
import { UserSearchSelect } from '@/components/common/UserSearchSelect'
import { invalidateAdminCaches } from '@/utils/cacheInvalidation'
import { dollarsToCents } from '@/utils/money'

interface Props {
  open: boolean
  onClose: () => void
}

export function AttestDepositModal({ open, onClose }: Props) {
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)

      const body: components['schemas']['AdminAttestRequest'] = {
        userId: values.userId,
        provider: values.provider,
        providerPaymentId: values.providerPaymentId,
        amountCents: dollarsToCents(values.amount),
        txHashes: values.txHashes ?? [],
        reason: values.reason ?? '',
      }

      await mutateNoBody(
        (parseAs) =>
          client.POST('/v2/admin/payments/attest', { body, parseAs }),
        'Failed to attest deposit',
      )
      void message.success('Late deposit attested and credited')
      form.resetFields()
      onClose()
      invalidateAdminCaches(queryClient)
    } catch (error) {
      if (error instanceof Error) {
        console.error(error)
        void message.error(error.message)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      title="Attest late deposit"
      open={open}
      onOk={() => void handleSubmit()}
      onCancel={onClose}
      confirmLoading={loading}
      okText="Attest & credit"
      destroyOnHidden
      width={520}
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item name="userId" label="User" rules={[{ required: true }]}>
          <UserSearchSelect
            style={{ width: '100%' }}
            onChange={(v) => form.setFieldValue('userId', v)}
          />
        </Form.Item>
        <Form.Item
          name="provider"
          label="Provider"
          rules={[{ required: true }]}
        >
          <Input placeholder="copperx" />
        </Form.Item>
        <Form.Item
          name="providerPaymentId"
          label="Provider payment ID"
          rules={[{ required: true }]}
        >
          <Input placeholder="External payment / session id" />
        </Form.Item>
        <Form.Item
          name="amount"
          label="Amount ($)"
          rules={[{ required: true, type: 'number', min: 0.01 }]}
        >
          <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="txHashes" label="Tx hashes">
          <Select
            mode="tags"
            tokenSeparators={[',', ' ']}
            placeholder="On-chain tx hashes (optional)"
          />
        </Form.Item>
        <Form.Item name="reason" label="Reason">
          <Input.TextArea rows={2} placeholder="Audit note (optional)" />
        </Form.Item>
      </Form>
    </Modal>
  )
}
